import { UNKNOWN_DATA_SOURCE_TYPE } from 'src/constants/dataSourceTypeDescription';
import {
  Project,
  ProjectVirtualGraph,
  ProjectsState,
} from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';

export const dataSourceTypeChanges: Record<string, string> = {
  // Data sources that were renamed
  APACHE_CASSANDRA: 'CASSANDRA',
  APACHE_CLOUDERA_IMPALA: 'IMPALA',
  APACHE_HIVE: 'HIVE',
  AWS_ATHENA: 'ATHENA',
  AWS_REDSHIFT: 'REDSHIFT',
  BIG_QUERY: 'BIGQUERY',
  DATABRICKS_SPARK: 'SPARKSQL',
  GENERIC_SQL: 'DEFAULT',
  IBM_DB2: 'DB2',
  MICROSOFT_SQL_SERVER: 'MSSQL',
  MONGO: 'MONGODB',
  SAP_HANA: 'HANA',
  SYBASE_ASE: 'SYBASE',

  // Data sources that combined with a different type
  COSMOS_DB: 'MONGODB',
  MARIADB: 'MYSQL',

  // Two versions of salesforce were added; we assume that
  // older versions will use the simba driver
  SALESFORCE: 'SALESFORCE_SIMBA',

  // New data sources
  EXASOL: 'EXASOL',
  GOOGLESHEETS: 'GOOGLESHEETS',
  DEFAULT_CDATA: 'DEFAULT_CDATA',
  JIRA: 'JIRA',
  LDAP_CDATA: 'LDAP_CDATA',
  REST: 'REST',
  SALESFORCE_CDATA: 'SALESFORCE_CDATA',
  SPARQL: 'SPARQL',
  SPLUNK: 'SPLUNK',

  // Unchanged data source names
  DERBY: 'DERBY',
  ELASTICSEARCH: 'ELASTICSEARCH',
  H2: 'H2',
  MYSQL: 'MYSQL',
  ORACLE: 'ORACLE',
  POSTGRESQL: 'POSTGRESQL',
  SNOWFLAKE: 'SNOWFLAKE',
  TERADATA: 'TERADATA',
  [UNKNOWN_DATA_SOURCE_TYPE]: UNKNOWN_DATA_SOURCE_TYPE,

  // Data sources that were removed
  AWS_AURORA_MYSQL: UNKNOWN_DATA_SOURCE_TYPE,
  AWS_AURORA_POSTGRESQL: UNKNOWN_DATA_SOURCE_TYPE,
};

/**
 * * on {@link ProjectVirtualGraph} rename data source types to follow the type description endpoint naming convention
 */
// prevState must be typed as `any` since it's an older version of the Redux State
export const v8 = (prevState: any): ReduxState => {
  const nextState = { ...prevState };

  if (nextState.projects) {
    nextState.projects = v8Projects(prevState.projects);
  }

  return nextState;
};

const v8Projects = (prevProjectsState: any): ProjectsState => {
  const projectsById: Record<string, Project> = {};

  Object.entries(
    prevProjectsState.projectsById as Record<string, Project>
  ).forEach(([projectId, project]) => {
    const virtualGraphsById: Record<string, ProjectVirtualGraph> = {};

    // This migration was not run when VET-1703 was released in v2.1.0.
    // We should not modify projects or VGs that were created after that release.
    const v220Date = new Date('2022-09-07T16:00:00-04:00');
    if (new Date(project.dateCreated) < v220Date) {
      Object.entries(project.virtualGraphsById).forEach(
        ([id, { dataSourceType, ...virtualGraph }]) => {
          virtualGraphsById[id] = {
            ...virtualGraph,
            dataSourceType:
              new Date(virtualGraph.dateLastUpdated) < v220Date
                ? dataSourceTypeChanges[
                    dataSourceType || UNKNOWN_DATA_SOURCE_TYPE
                  ] || UNKNOWN_DATA_SOURCE_TYPE
                : dataSourceType,
          };
        }
      );
    }

    projectsById[projectId] = {
      ...project,
      virtualGraphsById,
    };
  });

  return { ...prevProjectsState, projectsById };
};
